import { observable, action } from 'mobx';
import { ObjectDefinition, Target } from '../../types/DataSyncItem';
import { directoryStore } from '../DirectoryStore';
import { showToasts } from '../../lib/toaster';
import { FieldDefinition } from '../../types/FieldDefinition';
import { CGDestinationSchema } from '../../types/Schema';

export abstract class DataSyncObjectsStore {
  @observable public loading = false;
  @observable protected _sourceObjectDefinitions?: ObjectDefinition[];
  @observable protected _destinationObjectDefinitions?: ObjectDefinition[];
  @observable protected _transientDestinationObject?: ObjectDefinition;

  public constructor(protected target: Target, protected instanceId?: string) {
  }

  /**
   * Fetch and load object definitions
   */
  @action
  public async fetch() {
    try {
      this.loading = true;
      if (this.canFetch() && this.target === Target.SOURCE && !this._sourceObjectDefinitions) {
        this._sourceObjectDefinitions = await this.fetchSourceObjectDefinitions();
        this.sortObjectDefinitions(this._sourceObjectDefinitions);
      }
      if (this.canFetch() && this.target === Target.DESTINATION && !this._destinationObjectDefinitions) {
        await this.fetchAdditionalData?.();
        this._destinationObjectDefinitions = await this.fetchDestinationObjectDefinitions();
        this.sortObjectDefinitions(this._destinationObjectDefinitions);
      }
    } catch (e) {
      if (this.target === Target.SOURCE) {
        await this.handleSourceFetchError(e);
      } else {
        await this.handleDestinationFetchError(e);
      }
    } finally {
      this.loading = false;
    }
  }

  /**
   * Get the list of available source objects
   */
  public getSourceObjectList(
    configuredObjectName?: string,
    configuredObjectDisplayName?: string
  ): ObjectDefinition[] {
    const objects = this._sourceObjectDefinitions || [];

    // If configured object is provided and not in available list, prepend it as unavailable
    if (configuredObjectName && configuredObjectDisplayName) {
      const isAvailable = objects.some((obj) => obj.object_name === configuredObjectName);
      if (!isAvailable) {
        return [{
          object_name: configuredObjectName,
          object_display_name: configuredObjectDisplayName,
          object_icon_url: '',
          fields: [],
          disabled: true
        }, ...objects];
      }
    }

    return objects;
  }

  /**
   * Get the list of available destination objects
   */
  public getDestinationObjectList(
    configuredObjectName?: string,
    configuredObjectDisplayName?: string
  ): ObjectDefinition[] {
    const objects = this._destinationObjectDefinitions || [];

    // If configured object is provided and not in available list, prepend it as unavailable
    if (configuredObjectName && configuredObjectDisplayName) {
      const isAvailable = objects.some((obj) => obj.object_name === configuredObjectName);
      if (!isAvailable) {
        return [{
          object_name: configuredObjectName,
          object_display_name: configuredObjectDisplayName,
          object_icon_url: '',
          fields: [],
          disabled: true,
          warning: 'This object is no longer available. Please select a different object.'
        }, ...objects];
      }
    }

    return objects;
  }

  /**
   * Get fields for a specific object
   */
  public getSourceObjectFields(objectName: string): Promise<FieldDefinition[]> {
    const object = this._sourceObjectDefinitions?.find((obj) => obj.object_name === objectName);
    return Promise.resolve(object?.fields || []);
  }

  /**
   * Get fields for a specific object
   */
  @action
  public async getDestinationObjectFields(objectName: string): Promise<FieldDefinition[]> {
    if (!objectName) {
      return [];
    }

    const transientDestinationObject = this.getTransientDestinationObject();
    if (transientDestinationObject?.object_name === objectName) {
      return transientDestinationObject?.fields || [];
    }

    const object = this._destinationObjectDefinitions?.find((obj) => obj.object_name === objectName);
    return object?.fields || [];
  }

  /**
   * set transient destination object
   */
  @action
  public setTransientDestinationObject(objectDefinition?: ObjectDefinition): void {
    this._transientDestinationObject = objectDefinition;
  }

  public getTransientDestinationObject(): ObjectDefinition | undefined {
    return this._transientDestinationObject;
  }

  /**
   * perstst transient destination object
   */
  public async persistTransientDestinationObject?();

  /**
   * Get destination object definition by name
   * @param objectName
   * @returns ObjectDefinition or undefined if not found
   */
  public getDestinationObjectDefinition(objectName: string): ObjectDefinition | undefined {
    return this._destinationObjectDefinitions?.find((obj) => obj.object_name === objectName);
  }

  /**
   * Get source object definition by name
   * @param objectName
   * @returns ObjectDefinition or undefined if not found
   */
  public getSourceObjectDefinition(objectName: string): ObjectDefinition | undefined {
    return this._sourceObjectDefinitions?.find((obj) => obj.object_name === objectName);
  }

  /**
   * Check if source object is available
   * @param objectName
   * @returns boolean indicating if object exists in available list and is not disabled
   */
  public isSourceObjectAvailable(objectName: string): boolean {
    if (!objectName) {
      return true;
    }
    return this._sourceObjectDefinitions?.some((obj) => obj.object_name === objectName && !obj.disabled) || false;
  }

  /**
   * Check if destination object is available
   * @param objectName
   * @returns boolean indicating if object exists in available list and is not disabled
   */
  public isDestinationObjectAvailable(objectName: string): boolean {
    if (!objectName) {
      return true;
    }
    return this._destinationObjectDefinitions?.some((obj) => obj.object_name === objectName && !obj.disabled) || false;
  }

  /**
   * Get destination object schema
   * @param objectName
   * @returns DestinationSchema
   */
  public getDestinationObjectSchema(objectName: string): Promise<CGDestinationSchema | undefined> {
    return Promise.resolve(undefined);
  }

  /**
   * Fetch destination object definitions from the API
   */
   protected abstract fetchSourceObjectDefinitions(): Promise<ObjectDefinition[]>;

  /**
   * Fetch destination object definitions from the API
   */
  protected abstract fetchDestinationObjectDefinitions(): Promise<ObjectDefinition[]>;

  /**
   * Check if the store can fetch data
   */
  protected canFetch(): boolean {
    return !!directoryStore.trackerId && !!this.instanceId;
  }

  /**
   * Sort object definitions and their fields
   */
  protected sortObjectDefinitions(_objectDefinitions: ObjectDefinition[]): void {
    _objectDefinitions.forEach((object) => {
      if (object.fields) {
        object.fields = object.fields.sort((a, b) => a.name.localeCompare(b.name));
      }
    });
  }

  /**
   * Optional method for fetching additional data
   */
  protected async fetchAdditionalData?();

  /**
   * Handle errors during source schema fetch
   */
  protected async handleSourceFetchError(_error: any): Promise<void> {
    showToasts([{intent: 'danger', message: 'Unable to fetch source schema, please try again later.'}]);
  }

  /**
   * Handle errors during destination schema fetch
   */
  protected async handleDestinationFetchError(_error: any): Promise<void> {
    showToasts([{intent: 'danger', message: 'Unable to fetch destination schema, please try again later.'}]);
  }
}
